import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Wind, ShieldCheck, Wrench, MapPin, Lock, PlayCircle, BadgeCheck, Quote } from "lucide-react";
import api from "@/lib/api";
import { MaskedLines, Reveal, SectionHeading } from "@/components/motion";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { HOME_TESTIMONIALS } from "@/data/testimonials";

const CHAPTERS = [
  {
    num: "01", title: "The T-Post Problem",
    body: "Rope between two posts. Sagging vinyl, shredded hems, messages nobody can read. Every wind gust folds a conventional banner install in on itself — and your client's brand goes down with it.",
    img: "/assets/img/w_banner_fail_tposts.jpg",
  },
  {
    num: "02", title: "The Patented Bar",
    body: "U.S. Patent 10,410,553 B2. A telescoping aluminum bar locks the posts apart, so the frame physically cannot collapse inward under wind load. The banner stays taut, readable, professional.",
    img: "/assets/img/w-bar-img-7066_orig.jpg",
  },
  {
    num: "03", title: "The Complete System",
    body: "Precision-sized bungees with engineered give. T-clips that bite. UV-protected covers for a clean black finish. Every component exists to make the banner last longer and look better.",
    img: "/assets/img/w_twopost_completesystem.jpg",
  },
];

const STATS = [
  { k: "PATENTED", v: "10,410,553 B2" },
  { k: "ORIGIN", v: "MADE IN USA" },
  { k: "INSTALL", v: "≈ 15 MINUTES" },
  { k: "KIT RANGE", v: "4 FT — 16 FT" },
];

const KIT_PICKER = [
  { need: "4 ft curbside banner", kit: "Small Form", path: "/products/small-form-kit", testId: "pick-small-form" },
  { need: "6-8 ft roadside banner", kit: "Two Post", path: "/products/two-post-kit", testId: "pick-two-post" },
  { need: "12-16 ft highway banner", kit: "Three Post", path: "/products/three-post-kit", testId: "pick-three-post" },
  { need: "Two banners on a corner", kit: "V-Config", path: "/products/v-config-kit", testId: "pick-v-config" },
];

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.category === "kit"))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
          <img src="/assets/img/w-odp-hero-blue-sdi1677.jpg" alt="Close-up of The Banner Bar frame holding a blue banner against a clear sky" className="h-full w-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 h-[35%] bg-gradient-to-b from-transparent to-obsidian/90" />
          <div className="blueprint-grid absolute inset-x-0 bottom-0 h-[35%] opacity-40 [mask-image:linear-gradient(to_bottom,transparent,black)]" />
        </motion.div>

        <motion.div style={{ y: fgY }} className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-32 sm:px-6 lg:px-12">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:gap-10">
            <div className="clip-corner max-w-3xl bg-obsidian/70 px-6 py-6 backdrop-blur-md sm:px-10 sm:py-8" data-testid="hero-panel">
              <motion.p
                initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.2 } }}
                className="mb-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-hivis md:text-sm"
              >
                <Wind className="h-4 w-4" /> Wind-Resistant Banner Frame Systems
              </motion.p>
              <MaskedLines
                lines={["Your banner.", "Protected from", "the wind."]}
                delay={0.35}
                className="font-display text-[15vw] font-black uppercase leading-[0.88] tracking-tight text-slate-100 sm:text-7xl lg:text-[6.25rem]"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 1.0, duration: 0.7 } }}
              className="flex flex-col gap-4 sm:flex-row sm:items-center"
            >
              <Link to="/products" data-testid="hero-cta-button" className="clip-corner group flex items-center justify-center gap-3 bg-hivis px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors duration-200 hover:bg-hivis-hover">
                Shop the Kits <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 1.3 } }}
            className="mt-12 grid grid-cols-2 gap-px border border-girder bg-girder md:grid-cols-4"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.k} className="bg-obsidian/80 px-5 py-4 backdrop-blur">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{s.k}</p>
                <p className="mt-1 font-display text-lg font-bold uppercase text-slate-100">{s.v}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* MANIFESTO CHAPTERS */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="manifesto-section">
        <SectionHeading kicker="The Manifesto" title="The Banner Bar Solves the T-Post Problem" />
        <div className="mt-16 space-y-20">
          {CHAPTERS.map((c, i) => (
            <div key={c.num} className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <Reveal>
                <div className="flex items-start gap-6">
                  <span className="text-outline font-display text-7xl font-black leading-none lg:text-8xl">{c.num}</span>
                  <div>
                    <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-3xl">{c.title}</h3>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400 md:text-base">{c.body}</p>
                  </div>
                </div>
              </Reveal>
              <Reveal i={1}>
                <div className="clip-corner relative overflow-hidden border border-girder">
                  <img src={c.img} alt={c.title} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
                  <span className="absolute bottom-3 left-3 bg-obsidian/80 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-hivis backdrop-blur">FIG. {c.num}</span>
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED KITS */}
      <section className="border-y border-girder bg-steel/40 py-24 lg:py-32" data-testid="featured-kits-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading kicker="The Lineup" title="Four Kits. From Curbside to Highway." />
            <Reveal i={2}>
              <Link to="/compare" data-testid="compare-kits-cta" className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-hivis">
                Compare all kits <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal i={1}>
            <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.25em] text-hivis">Which kit fits you?</p>
            <div className="mt-3 grid gap-px border border-girder bg-girder sm:grid-cols-2 lg:grid-cols-4" data-testid="kit-picker">
              {KIT_PICKER.map((k) => (
                <Link key={k.path} to={k.path} data-testid={k.testId} className="group flex items-center justify-between gap-3 bg-obsidian/80 px-5 py-4 transition-colors hover:bg-steel">
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{k.need}</span>
                    <span className="mt-1 block font-display text-lg font-bold uppercase text-slate-100 transition-colors group-hover:text-hivis">{k.kit}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-hivis transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {products.map((p, i) => <ProductCard key={p.slug} product={p} i={i} />)}
          </div>
        </div>
      </section>

      {/* WHOLESALE BAND */}
      <section className="relative overflow-hidden py-24 lg:py-28" data-testid="wholesale-band">
        <div className="blueprint-grid absolute inset-0 opacity-30" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <SectionHeading kicker="Sign Industry" title="Dealer Pricing." />
            <Reveal i={2}>
              <p className="mt-6 max-w-lg text-sm leading-relaxed text-slate-400 md:text-base">
                Sign shops, wide format printers and installers buy The Banner Bar at wholesale. Apply once, get approved, and every price on this site switches to your dealer rate.
              </p>
            </Reveal>
            <Reveal i={3}>
              <Link to="/dealer-portal" data-testid="wholesale-apply-cta" className="clip-corner mt-8 inline-flex items-center gap-3 bg-hivis px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover">
                <Lock className="h-5 w-5" /> Apply for Wholesale
              </Link>
            </Reveal>
          </div>
          <Reveal i={1}>
            <div className="clip-corner relative border border-girder">
              <img src="/assets/img/lr-all-brands-dealer-poles.jpg" alt="Banner Bar poles custom-branded for dealers including FastSigns, Arrowhead Signs and Displays, Marked By Imagination, and Blackout Graphics" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-obsidian/85 px-4 py-3 backdrop-blur">
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Retail $376.00</span>
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-hivis"><Lock className="h-3 w-3" /> Dealer rate locked</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CORPORATE TEASER */}
      <section className="border-y border-girder bg-steel/40 py-24 lg:py-28" data-testid="corporate-teaser">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <Reveal className="order-2 lg:order-1">
            <div className="grid grid-cols-2 gap-4">
              <img src="/assets/img/w_bungee_detail.jpg" alt="Close-up of Banner Bar T-clip and bungee connection" loading="lazy" className="clip-corner aspect-[3/4] w-full border border-girder object-cover" />
              <img src="/assets/img/w_ribcity_vertical.jpg" alt="Banner Bar frame at Rib City restaurant location" loading="lazy" className="clip-corner mt-8 aspect-[3/4] w-full border border-girder object-cover" />
            </div>
          </Reveal>
          <div className="order-1 lg:order-2">
            <SectionHeading kicker="Corporate & Franchise" title="One frame standard. Every location." />
            <Reveal i={2}>
              <p className="mt-6 max-w-lg text-sm leading-relaxed text-slate-400 md:text-base">
                Roll out identical, professional banner displays across 10 or 1,000 locations. Volume pricing, drop-ship fulfillment to each store, and easy installation by facility maintenance or grounds crews.
              </p>
            </Reveal>
            <Reveal i={3}>
              <ul className="mt-6 space-y-3">
                {["Volume and multi-location pricing", "Ship kits direct to every franchise address", "Uniform look — same frame, every store"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                    <BadgeCheck className="h-4 w-4 shrink-0 text-hivis" /> {t}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal i={4}>
              <Link to="/corporate" data-testid="corporate-teaser-cta" className="clip-line mt-8 inline-flex items-center gap-3 [--cb-line:#F59E0B] px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-hivis transition-colors hover:text-obsidian hover:[--cb-bg:#F59E0B]">
                Plan a Rollout <ArrowRight className="h-5 w-5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INSTALL PREVIEW */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="install-preview">
        <SectionHeading kicker="See It Work" title="Fifteen minutes. Banner installed." />
        <div className="mt-12 grid items-center gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="clip-corner relative overflow-hidden border border-girder bg-black">
              <video src="/assets/img/two-post-install-hs.mp4" poster="/assets/img/two-post-install-poster.jpg" controls preload="metadata" className="aspect-video w-full" data-testid="install-video" />
            </div>
          </Reveal>
          <div className="lg:col-span-5">
            {[
              { icon: Wrench, t: "Drive two T-posts", d: "Use the telescoping bar itself as your measuring jig." },
              { icon: ShieldCheck, t: "Lock the bar in place", d: "T-clips lock onto the posts - the frame cannot collapse inward." },
              { icon: Wind, t: "Hang Your Banner", d: "Sized bungees hold tension with engineered give in wind gusts." },
            ].map((s, i) => (
              <Reveal key={s.t} i={i}>
                <div className="flex gap-4 border-b border-girder py-5 first:pt-0">
                  <s.icon className="mt-1 h-5 w-5 shrink-0 text-hivis" />
                  <div>
                    <h4 className="font-display text-lg font-bold uppercase text-slate-100">{s.t}</h4>
                    <p className="mt-1 text-sm text-slate-400">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal i={3}>
              <Link to="/installation" data-testid="install-more-cta" className="group mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-hivis">
                <PlayCircle className="h-4 w-4" /> Full install library <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PARTS & REPLACEMENTS */}
      <section className="border-t border-girder py-24 lg:py-28" data-testid="parts-section">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <SectionHeading kicker="Wear Parts" title="Replace the bungee, not the frame." />
            <Reveal i={2}>
              <p className="mt-6 max-w-lg text-sm leading-relaxed text-slate-400 md:text-base">
                Bungees are the wear item by design — expect 2-3 months in heavy summer UV, longer in mild seasons. When one wears out, you swap a bungee, not a frame. Full parts inventory — bungees, T-clips, pole covers — in stock and shipping from the USA.
              </p>
            </Reveal>
            <Reveal i={3}>
              <p className="mt-4 max-w-lg font-mono text-[11px] uppercase tracking-widest text-slate-500">Dealers: every reorder is repeat revenue for your shop</p>
            </Reveal>
            <Reveal i={4}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/products/replacement-bungees" data-testid="parts-order-cta" className="clip-corner inline-flex items-center gap-3 bg-hivis px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover">
                  Order replacement bungees <ArrowRight className="h-5 w-5" />
                </Link>
                <Link to="/quality-guarantee" data-testid="parts-guarantee-link" className="font-mono text-xs uppercase tracking-widest text-slate-300 underline decoration-hivis underline-offset-4 hover:text-hivis">Backed by the guarantee</Link>
              </div>
            </Reveal>
          </div>
          <Reveal i={1}>
            <div className="clip-corner relative overflow-hidden border border-girder">
              <img src="/assets/img/w-top-bungees_orig.jpg" alt="Replacement Banner Bar bungees with hang tag" loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
              <span className="absolute bottom-3 left-3 bg-obsidian/80 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-hivis backdrop-blur">In stock · Ships from USA</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-girder py-24 lg:py-28" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <SectionHeading kicker="Field Tested" title="What owners say." />
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {HOME_TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} i={i % 3}>
                <figure className="clip-corner flex h-full flex-col border border-girder bg-steel/40 p-7" data-testid={`testimonial-${i + 1}`}>
                  <div className="flex items-center gap-3">
                    <Quote className="h-5 w-5 shrink-0 text-hivis" />
                    {t.logo && (
                      <img src={t.logo} alt={`${t.company} logo`} className={`${t.wordmark ? "h-5" : "h-9"} w-auto object-contain`} loading="lazy" />
                    )}
                  </div>
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">{t.quote}</blockquote>
                  <figcaption className="mt-6 border-t border-girder pt-4">
                    <p className="font-display text-sm font-bold uppercase tracking-wide text-slate-100">{t.name}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{t.role} · {t.company}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DEALER FINDER + GUARANTEE */}
      <section className="border-t border-girder bg-steel/40 py-24" data-testid="locator-guarantee">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <Reveal className="h-full">
            <div className="clip-corner flex h-full flex-col overflow-hidden border border-girder bg-obsidian">
              <img src="/assets/img/w_buylocal_ribcity.jpg" alt="Banner Bar install standing beside a fallen rope-hung banner" loading="lazy" className="min-h-0 w-full flex-1 object-cover" />
              <div className="p-8 lg:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-hivis">Buy Local</p>
              <h3 className="mt-3 font-display text-3xl font-black uppercase text-slate-100">Find Your Local Sign Shop</h3>
              <p className="mt-3 text-sm text-slate-400">Sign shops in our authorized dealer network carry The Banner Bar and can point you to the right heavy-duty T-posts.</p>
              <p className="mt-6 flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-hivis"><MapPin className="h-4 w-4" /> 75+ authorized dealers nationwide</p>
              <Link to="/dealer-locator" data-testid="home-dealer-cta" className="clip-corner-sm mt-4 inline-flex items-center gap-2 bg-hivis px-6 py-3.5 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">
                Find your local shop <ArrowRight className="h-4 w-4" />
              </Link>
              </div>
            </div>
          </Reveal>
          <Reveal i={1} className="h-full">
            <div className="clip-corner relative h-full overflow-hidden border border-girder">
              <img src="/assets/img/us-patent-made-in-usa-01.png" alt="US Patent and Made in USA badge" className="absolute right-6 top-6 z-10 h-24 w-24 object-contain" />
              <img src="/assets/img/w_fastsigns_bar.jpg" alt="Banner Bar hardware close-up with FastSigns branded bar" loading="lazy" className="h-full min-h-[280px] w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-obsidian to-transparent p-8 pt-20">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-hivis">Quality Guarantee</p>
                <p className="mt-2 max-w-md text-sm text-slate-300">Guaranteed to increase the life of your banner when installed correctly — especially in windy conditions.</p>
                <Link to="/quality-guarantee" data-testid="guarantee-cta" className="mt-3 inline-block font-mono text-xs uppercase tracking-widest text-slate-100 underline decoration-hivis underline-offset-4 hover:text-hivis">Read the guarantee</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
